// 加工指示サマリ一覧(区分バンド/アンカーグリッド行)の組み立てと再取得をまとめる。
// 本文は HinbanMaster.js から移設したもの。

export function createKakousijiSummaryListService(deps) {
    const {
        DeepCopy,
        DicVal,
        GridData,
        KAKOUSIJI_ANCHOR_PANEL_TYPE,
        KAKOUSIJI_SAMPLE_GROUP_KEY,
        KAKOUSIJI_S_LO_TYPE_OFFSET,
        activeKakousijiKey,
        activeKakousijiSummaryItem,
        buildKakousijiKnownColumnIds,
        collectKakousijiPanelColumnIds,
        entrydataRef,
        firstNonEmptyValue,
        fnc,
        getKakousijiEntryCache,
        getKakousijiEntryCacheForSummaryItem,
        gridlist,
        isEmpty,
        isKakousijiPanelType,
        isKakousijiSampleKubunValue,
        kakousijiImportedSummaryRowsRef,
        kakousijiSummaryList,
        normalizeKakousijiKubunValue,
        normalizeKakousijiSampleDetailForStoreByType,
        resolveKakousijiGridPanelByType,
        resolveKakousijiSummaryKubunLabel,
        resolvePanelTypeColumnIdByName,
        setActiveKakousijiKey,
        setKakousijiGridRows,
        setKakousijiSummaryList,
        setKakousijiSummaryLoading
    } = deps;

    function normalizeKakousijiSummaryRow(row, index) {
        const sample = isKakousijiSampleKubunValue(DicVal(row, "kubunValue", ""));
        const keyCandidate = String(DicVal(row, "key", DicVal(row, "code", ""))).trim();
        const key = isEmpty(keyCandidate) ? `kakousiji-${index}` : keyCandidate;
        const sLoType = String(DicVal(row, "sLoType", "")).trim();
        const sectionNo = /^\d+$/.test(sLoType)
            ? String(Number(sLoType) + KAKOUSIJI_S_LO_TYPE_OFFSET)
            : "";
        const supplierCode = String(DicVal(row, "supplierCode", "")).trim();
        const code = String(DicVal(row, "code", "")).trim();
        return {
            ...row,
            key: key,
            code: code,
            oppiCode: String(DicVal(row, "oppiCode", "")).trim(),
            kubunValue: normalizeKakousijiKubunValue(DicVal(row, "kubunValue", "")),
            supplierCode: supplierCode,
            supplierName: String(DicVal(row, "supplierName", "")).trim(),
            updated: String(DicVal(row, "updated", "")).trim(),
            isDraft: !!DicVal(row, "isDraft", false),
            isSample: sample,
            kubunLabel: resolveKakousijiSummaryKubunLabel(row, index),
            summaryLabel: sample ? String(DicVal(row, "summaryLabel", "仕入先")).trim() : "加工指示番号",
            summaryValue: sample ? String(DicVal(row, "summaryValue", supplierCode)).trim() : code,
            sectionNo: sectionNo
        };
    }
    function ensureKakousijiSummaryRows(rows) {
        return Array.isArray(rows) ? rows : [];
    }
    function isKakousijiSampleGroupSummaryItem(item) {
        if (isEmpty(item)) return false;
        if (String(DicVal(item, 'key', '')).trim() === KAKOUSIJI_SAMPLE_GROUP_KEY) return true;
        return Array.isArray(DicVal(item, 'sampleEntryKeys', null));
    }
    function kakousijiSampleGroupEntryKeys(item) {
        return (Array.isArray(DicVal(item, 'sampleEntryKeys', null)) ? DicVal(item, 'sampleEntryKeys', []) : [])
            .map((key) => String(key ?? '').trim())
            .filter((key, index, self) => key !== '' && self.indexOf(key) === index);
    }
    function activeKakousijiSampleGroupEntryKey(item) {
        const keys = kakousijiSampleGroupEntryKeys(item);
        if (keys.length === 0) return '';
        const activeKey = String(DicVal(item, 'activeSampleEntryKey', '')).trim();
        return keys.includes(activeKey) ? activeKey : keys[keys.length - 1];
    }
    function buildKakousijiSampleGroupSummaryItem(sourceItem, sampleEntryKeys, activeSampleEntryKey, index) {
        const keys = (Array.isArray(sampleEntryKeys) ? sampleEntryKeys : [])
            .map((key) => String(key ?? '').trim())
            .filter((key, keyIndex, self) => key !== '' && self.indexOf(key) === keyIndex);
        const activeKey = keys.includes(String(activeSampleEntryKey ?? '').trim())
            ? String(activeSampleEntryKey ?? '').trim()
            : (keys.length === 0 ? '' : keys[keys.length - 1]);
        const firstSampleSnapshot = keys.length === 0 ? null : getKakousijiEntryCache(keys[0]);
        const firstSampleData = DicVal(firstSampleSnapshot, 'data', {});
        const firstSupplier = String(DicVal(firstSampleData, 'SSD_SIIRECD', '')).trim();
        return normalizeKakousijiSummaryRow({
            ...(isEmpty(sourceItem) ? {} : sourceItem),
            key: KAKOUSIJI_SAMPLE_GROUP_KEY,
            code: '',
            kubunValue: '0',
            supplierCode: '',
            summaryLabel: '仕入先:',
            summaryValue: firstSupplier === '' ? '未設定' : firstSupplier,
            sampleEntryKeys: keys,
            activeSampleEntryKey: activeKey,
            isDraft: keys.some((key) => String(key).indexOf('draft-') === 0 || String(key).indexOf('sample-draft-') === 0)
        }, index);
    }
    function groupKakousijiSampleSummaryRows(rows) {
        const sourceRows = Array.isArray(rows) ? rows : [];
        const nonSampleRows = [];
        const sampleKeys = [];
        let sampleSourceItem = null;
        let activeSampleKey = '';
        sourceRows.forEach((row) => {
            if (!isKakousijiSampleKubunValue(DicVal(row, 'kubunValue', '')) && !isKakousijiSampleGroupSummaryItem(row)) {
                nonSampleRows.push(row);
                return;
            }
            if (sampleSourceItem === null) sampleSourceItem = row;
            const rowKeys = isKakousijiSampleGroupSummaryItem(row)
                ? kakousijiSampleGroupEntryKeys(row)
                : [String(DicVal(row, 'key', DicVal(row, 'code', ''))).trim()];
            rowKeys.forEach((key) => {
                if (key !== '' && !sampleKeys.includes(key)) sampleKeys.push(key);
            });
            const rowActiveKey = activeKakousijiSampleGroupEntryKey(row);
            if (rowActiveKey !== '') activeSampleKey = rowActiveKey;
        });
        if (sampleKeys.length === 0) return nonSampleRows;
        return [
            ...nonSampleRows,
            buildKakousijiSampleGroupSummaryItem(sampleSourceItem, sampleKeys, activeSampleKey === '' ? sampleKeys[0] : activeSampleKey, nonSampleRows.length)
        ];
    }
    function mergeKakousijiSummaryRows(primaryRows, fallbackRows) {
        const merged = [];
        const usedKeys = {};
        const append = (row) => {
            if (isEmpty(row)) return;
            const code = String(DicVal(row, 'code', '')).trim();
            const key = code === '' ? String(DicVal(row, 'key', '')).trim() : code;
            if (key === '' || usedKeys[key]) return;
            usedKeys[key] = true;
            merged.push(row);
        };
        (Array.isArray(primaryRows) ? primaryRows : []).forEach(append);
        (Array.isArray(fallbackRows) ? fallbackRows : []).forEach(append);
        return merged;
    }
    function buildKakousijiSummaryRowFromEntry(entry, index, fallbackOppiCode) {
        const data = DicVal(entry, 'data', {});
        const detail = DicVal(entry, 'detail', {});
        const ssdxRows = DicVal(detail, '108', []);
        const firstSsdxRow = Array.isArray(ssdxRows) && ssdxRows.length !== 0 ? DicVal(ssdxRows, 0, {}) : {};
        const code = String(DicVal(entry, 'SSD_NO', DicVal(data, 'SSD_NO', ''))).trim();
        if (code === '') return null;
        return normalizeKakousijiSummaryRow({
            key: code,
            code: code,
            oppiCode: String(DicVal(entry, 'SSD_OPPICD', DicVal(data, 'SSD_OPPICD', fallbackOppiCode))).trim(),
            kubunValue: String(firstNonEmptyValue(data, ['SSDX_KBN']) || firstNonEmptyValue(firstSsdxRow, ['SSDX_KBN']) || '1').trim(),
            supplierCode: String(DicVal(data, 'SSD_SIIRECD', '')).trim(),
            supplierName: String(DicVal(data, 'SSD_SIIRENM', DicVal(data, 'TOR_NAME', ''))).trim(),
            updated: String(DicVal(data, 'SSD_UPDATE', '')).trim()
        }, index);
    }
    function buildKakousijiSummaryRowFromSnapshot(summaryItem, snapshot, index) {
        const item = isEmpty(summaryItem) ? {} : summaryItem;
        const data = DicVal(snapshot, 'data', {});
        const detail = DicVal(snapshot, 'detail', {});
        const ssdxRows = DicVal(detail, '108', []);
        const firstSsdxRow = Array.isArray(ssdxRows) && ssdxRows.length !== 0 ? DicVal(ssdxRows, 0, {}) : {};
        const fallbackCode = String(DicVal(item, 'code', DicVal(item, 'SSD_NO', ''))).trim();
        const code = String(DicVal(data, 'SSD_NO', DicVal(data, 'SSD_SIJINO', fallbackCode))).trim();
        const key = String(DicVal(item, 'key', code)).trim();
        const rawKubunValue = String(firstNonEmptyValue(data, ['SSDX_KBN']) || firstNonEmptyValue(firstSsdxRow, ['SSDX_KBN']) || DicVal(item, 'kubunValue', '')).trim();
        const normalizedRow = normalizeKakousijiSummaryRow({
            ...item,
            key: key === '' ? code : key,
            code: code,
            oppiCode: String(DicVal(data, 'SSD_OPPICD', DicVal(item, 'oppiCode', ''))).trim(),
            kubunValue: rawKubunValue,
            supplierCode: String(DicVal(data, 'SSD_SIIRECD', DicVal(item, 'supplierCode', ''))).trim(),
            supplierName: String(DicVal(data, 'SSD_SIIRENM', DicVal(data, 'TOR_NAME', DicVal(item, 'supplierName', '')))).trim(),
            updated: String(DicVal(data, 'SSD_UPDATE', DicVal(item, 'updated', ''))).trim()
        }, index);
        return normalizedRow;
    }
    function buildLiveActiveKakousijiSnapshot() {
        const activeItem = activeKakousijiSummaryItem();
        if (isEmpty(activeItem)) return null;
        const data = {};
        collectKakousijiPanelColumnIds().forEach((columnId) => {
            data[columnId] = DicVal(entrydataRef.current, columnId, '');
        });
        const detail = {};
        (Array.isArray(gridlist) ? gridlist : []).forEach((item) => {
            if (!isKakousijiPanelType(DicVal(item, 'type', ''))) return;
            const type = String(DicVal(item, 'type', '')).trim();
            detail[type] = DeepCopy(normalizeKakousijiSampleDetailForStoreByType(type, GridData(item) || []));
        });
        return { data: data, detail: detail };
    }
    function getKakousijiSnapshotForSummaryItem(summaryItem) {
        if (isEmpty(summaryItem)) return null;
        if (String(DicVal(summaryItem, 'key', '')).trim() === String(activeKakousijiKey ?? '').trim()) {
            return buildLiveActiveKakousijiSnapshot();
        }
        return getKakousijiEntryCacheForSummaryItem(summaryItem);
    }
    function expandKakousijiSummaryItemsForAnchor(summaryItems) {
        const sourceItems = Array.isArray(summaryItems) ? summaryItems : [];
        const expanded = [];
        sourceItems.forEach((item) => {
            if (!isKakousijiSampleGroupSummaryItem(item)) {
                expanded.push(item);
                return;
            }
            kakousijiSampleGroupEntryKeys(item).forEach((entryKey, index) => {
                const snapshot = getKakousijiEntryCache(entryKey);
                if (isEmpty(snapshot)) return;
                expanded.push(buildKakousijiSummaryRowFromSnapshot({
                    ...item,
                    key: entryKey,
                    code: String(DicVal(DicVal(snapshot, 'data', {}), 'SSD_NO', DicVal(DicVal(snapshot, 'data', {}), 'SSD_SIJINO', entryKey))).trim(),
                    sampleEntryKeys: undefined,
                    activeSampleEntryKey: undefined
                }, snapshot, expanded.length + index));
            });
        });
        return expanded;
    }
    function buildKakousijiAnchorGridRowsFromSummary(mainData, summaryItems) {
        const baseData = isEmpty(mainData) ? {} : mainData;
        const sourceItems = expandKakousijiSummaryItemsForAnchor(summaryItems);
        const kubunCol = resolvePanelTypeColumnIdByName(KAKOUSIJI_ANCHOR_PANEL_TYPE, ['加工指示区分']);
        const noCol = resolvePanelTypeColumnIdByName(KAKOUSIJI_ANCHOR_PANEL_TYPE, ['# 加工指示番号', '#加工指示番号', '加工指示番号']);
        return sourceItems.map((item, index) => {
            const snapshot = getKakousijiSnapshotForSummaryItem(item);
            const rowData = DeepCopy(DicVal(snapshot, 'data', {}));
            const summaryRow = isEmpty(snapshot)
                ? normalizeKakousijiSummaryRow({ ...item }, index)
                : buildKakousijiSummaryRowFromSnapshot(item, snapshot, index);
            const row = {
                ...DeepCopy(baseData),
                ...rowData,
                rowid: 'kakousiji_anchor_' + String(index + 1)
            };
            const ssdNo = String(DicVal(summaryRow, 'code', DicVal(rowData, 'SSD_NO', ''))).trim();
            if (ssdNo !== '') {
                row.SSD_NO = ssdNo;
                row.SSD_SIJINO = ssdNo;
            }
            const kubunValue = String(DicVal(summaryRow, 'kubunValue', DicVal(rowData, 'SSDX_KBN', ''))).trim();
            if (kubunValue !== '') row.SSDX_KBN = kubunValue;
            if (kubunCol !== '') row[kubunCol] = String(DicVal(summaryRow, 'kubunLabel', '')).trim();
            if (noCol !== '') row[noCol] = ssdNo;
            return row;
        });
    }
    function syncKakousijiAnchorGridRows(summaryItems) {
        const anchorPanel = resolveKakousijiGridPanelByType(KAKOUSIJI_ANCHOR_PANEL_TYPE);
        if (isEmpty(anchorPanel)) return;
        const sourceItems = Array.isArray(summaryItems) ? summaryItems : kakousijiSummaryList;
        setKakousijiGridRows(anchorPanel, buildKakousijiAnchorGridRowsFromSummary(entrydataRef.current, sourceItems));
    }
    // 同じ内容でも新しい配列を返すため、そのまま setState すると全パネルが再描画される。
    // マスタ照会の応答ごとに呼ばれるので、内容が変わらないときは state を更新しない。
    function isSameKakousijiSummaryList(before, after) {
        if (before === after) return true;
        if (!Array.isArray(before) || !Array.isArray(after)) return false;
        if (before.length !== after.length) return false;
        try {
            return JSON.stringify(before) === JSON.stringify(after);
        } catch (error) {
            return false;
        }
    }
    function refreshKakousijiSummaryState(targetSsdNo = '') {
        const normalizedTargetSsdNo = String(targetSsdNo ?? '').trim();
        const sourceItems = Array.isArray(kakousijiSummaryList) ? kakousijiSummaryList : [];
        const nextSummaryItems = sourceItems.map((item, index) => {
            const itemCode = String(DicVal(item, 'code', '')).trim();
            const shouldRefresh = normalizedTargetSsdNo === '' || normalizedTargetSsdNo === itemCode;
            if (!shouldRefresh) return normalizeKakousijiSummaryRow({ ...item }, index);
            const snapshot = getKakousijiSnapshotForSummaryItem(item);
            if (isEmpty(snapshot)) return normalizeKakousijiSummaryRow({ ...item }, index);
            return buildKakousijiSummaryRowFromSnapshot(item, snapshot, index);
        });
        if (!isSameKakousijiSummaryList(sourceItems, nextSummaryItems)) {
            setKakousijiSummaryList(nextSummaryItems);
        }
        syncKakousijiAnchorGridRows(nextSummaryItems);
    }
    function resolveKakousijiSummaryTargetSsdNo(targetData) {
        const data = isEmpty(targetData) ? {} : targetData;
        const fromData = String(DicVal(data, 'SSD_NO', DicVal(data, 'SSD_SIJINO', ''))).trim();
        if (fromData !== '') return fromData;
        return String(DicVal(activeKakousijiSummaryItem(), 'code', '')).trim();
    }
    function shouldSyncKakousijiSummaryByFieldId(fieldId) {
        const normalized = String(fieldId ?? '').trim().toUpperCase();
        if (normalized === '') return false;
        if (normalized.indexOf('SSD') === 0) return true;
        return buildKakousijiKnownColumnIds(KAKOUSIJI_ANCHOR_PANEL_TYPE).includes(normalized);
    }
    function buildKakousijiAnchorGridRows(mainData, entries) {
        const sourceEntries = Array.isArray(entries) ? entries : [];
        const baseData = isEmpty(mainData) ? {} : mainData;
        const kubunCol = resolvePanelTypeColumnIdByName(KAKOUSIJI_ANCHOR_PANEL_TYPE, ['加工指示区分']);
        const noCol = resolvePanelTypeColumnIdByName(KAKOUSIJI_ANCHOR_PANEL_TYPE, ['# 加工指示番号', '#加工指示番号', '加工指示番号']);
        return sourceEntries.map((entry, index) => {
            const rowData = DeepCopy(DicVal(entry, 'data', {}));
            const summaryRow = buildKakousijiSummaryRowFromEntry(entry, index, String(DicVal(baseData, 'PDT_OPPICD', '')).trim());
            const row = {
                ...DeepCopy(baseData),
                ...rowData,
                rowid: 'kakousiji_anchor_' + String(index + 1)
            };
            const ssdNo = String(DicVal(entry, 'SSD_NO', DicVal(rowData, 'SSD_NO', ''))).trim();
            if (ssdNo !== '') {
                row.SSD_NO = ssdNo;
                row.SSD_SIJINO = ssdNo;
            }
            const kubunValue = String(DicVal(rowData, 'SSDX_KBN', DicVal(summaryRow, 'kubunValue', ''))).trim();
            if (kubunValue !== '') row.SSDX_KBN = kubunValue;
            if (kubunCol !== '') row[kubunCol] = String(DicVal(summaryRow, 'kubunLabel', '')).trim();
            if (noCol !== '') row[noCol] = ssdNo;
            return row;
        });
    }
    function loadKakousijiSummaryList(oppiCode, options) {
        const requestCode = String(isEmpty(oppiCode) ? "" : oppiCode).trim();
        const silent = !!DicVal(options, "silent", true);
        const preferredCode = String(DicVal(options, "preferredCode", "")).trim();
        if (isEmpty(requestCode)) {
            const rows = ensureKakousijiSummaryRows([]);
            setKakousijiSummaryList(rows);
            setActiveKakousijiKey("");
            setKakousijiSummaryLoading(false);
            return;
        }
        setKakousijiSummaryLoading(true);
        fnc.current.postaxios("hinbanmaster/GetKakousijiSummaryList",
            { OppiCode: requestCode },
            {
                onSuccess: function (data) {
                    try {
                        const source = Array.isArray(DicVal(data.data, "rows", null)) ? DicVal(data.data, "rows", []) : [];
                        const normalizedRows = ensureKakousijiSummaryRows(source.map((row, index) => normalizeKakousijiSummaryRow(row, index)));
                        const rows = groupKakousijiSampleSummaryRows(mergeKakousijiSummaryRows(normalizedRows, kakousijiImportedSummaryRowsRef.current));
                        setKakousijiSummaryList(rows);
                        syncKakousijiAnchorGridRows(rows);
                        setActiveKakousijiKey((prev) => {
                            if (preferredCode !== "") {
                                const preferred = rows.find((row) => String(DicVal(row, "code", "")).trim() === preferredCode);
                                if (!isEmpty(preferred)) return preferred.key;
                            }
                            return rows.some((row) => row.key === prev) ? prev : (rows.length === 0 ? "" : rows[0].key);
                        });
                    } catch (e) {
                        console.error(e);
                    } finally {
                        setKakousijiSummaryLoading(false);
                    }
                },
                onError: function (data) {
                    setKakousijiSummaryLoading(false);
                    if (!silent) {
                        let msg = "加工指示サマリーの取得に失敗しました";
                        if (!isEmpty(data.errDescription)) msg = data.errDescription;
                        fnc.current.warnmsg(msg);
                    }
                }
            }, true
        );
    }

    return {
        normalizeKakousijiSummaryRow,
        ensureKakousijiSummaryRows,
        isKakousijiSampleGroupSummaryItem,
        kakousijiSampleGroupEntryKeys,
        activeKakousijiSampleGroupEntryKey,
        buildKakousijiSampleGroupSummaryItem,
        groupKakousijiSampleSummaryRows,
        mergeKakousijiSummaryRows,
        buildKakousijiSummaryRowFromEntry,
        buildKakousijiSummaryRowFromSnapshot,
        buildLiveActiveKakousijiSnapshot,
        getKakousijiSnapshotForSummaryItem,
        expandKakousijiSummaryItemsForAnchor,
        buildKakousijiAnchorGridRowsFromSummary,
        syncKakousijiAnchorGridRows,
        refreshKakousijiSummaryState,
        resolveKakousijiSummaryTargetSsdNo,
        shouldSyncKakousijiSummaryByFieldId,
        buildKakousijiAnchorGridRows,
        loadKakousijiSummaryList
    };
}
